import * as Sentry from "@sentry/browser";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    type ColumnDef,
    getCoreRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { useDebounce } from "@uidotdev/usehooks";
import { useAtom } from "jotai";
import { useEffect, useMemo } from "react";
import { camelToSnakeCase } from "../../utils/string";
import {
    filterStateToApiParams,
    type TableState,
    tableStateFamily,
} from "./state";

const buildSortingValue = <T>(
    tableState: TableState,
    columns: ColumnDef<T, any>[],
) => {
    const sortingKeys: string[] = [];
    for (const sortedColumn of tableState.sorting ?? []) {
        const columnDef = columns.find((c) => c.id === sortedColumn.id);
        // If the column exists, is sortable and visible
        if (
            columnDef &&
            columnDef.enableSorting !== false &&
            tableState.columnVisibility?.[sortedColumn.id] !== false
        ) {
            // Either sorting key or conversion from camelCase to snake_case of id
            const sortingKey =
                columnDef.meta?.sortingKey ?? camelToSnakeCase(sortedColumn.id);
            sortingKeys.push(`${sortedColumn.desc ? "-" : ""}${sortingKey}`);
        }
    }
    if (sortingKeys.length === 0) {
        return undefined;
    }
    return sortingKeys.join(",");
};

export interface UseDataTableV3Options<T> {
    dataName: string;
    // Shared config id, defaults to dataName. See `tableStateFamily`.
    configId?: string;
    columns: ColumnDef<T, any>[];
    fetchFunction: any;
    sortable: boolean;
    filterable: boolean;
    extraFilters: {
        [key: string]: any;
    };
    defaultPageSize?: number;
    partialUpdate?: (id: string, value: Partial<T>) => Promise<void>;
    deleteRow?: (id: string) => Promise<void>;
    getRowId?: (row: T) => any;
    selectable?: boolean;
}

/**
 * Custom hook that encapsulates all DataTableV3 logic including:
 * - Data fetching with react-query
 * - State management (pagination, sorting, filtering) with Jotai
 * - Table instance creation with TanStack Table
 * - CRUD operations (partial updates, delete)
 *
 * This hook can be used with any UI implementation.
 */
export const useDataTableV3 = <T>(options: UseDataTableV3Options<T>) => {
    const {
        dataName,
        configId,
        columns,
        fetchFunction,
        sortable,
        filterable,
        selectable,
        extraFilters,
        partialUpdate: partialUpdateFn,
        deleteRow: deleteRowFn,
        getRowId = (row: any) => row.id,
    } = options;

    const defaultData = useMemo(() => [], []);
    const [tableState, setTableState] = useAtom(
        tableStateFamily({
            tableId: dataName,
            configId,
        }),
    );

    /**
     * Query builder & fetch/update functions
     */
    const fetchDataOptions = useMemo(() => {
        return {
            ...extraFilters,
            ...filterStateToApiParams(tableState.filters),
            ordering: buildSortingValue(tableState, columns),
            page: tableState.pagination.pageIndex + 1,
            pageSize: tableState.pagination.pageSize,
        };
    }, [tableState, extraFilters, columns]);

    const queryClient = useQueryClient();
    const debouncedQueryParams = useDebounce(fetchDataOptions, 400);
    const dataQuery = useQuery(
        [dataName, debouncedQueryParams],
        () => fetchFunction(debouncedQueryParams),
        {
            keepPreviousData: true,
            retry: 1,
        },
    );

    // Handle when pages don't exist, resetting the pagination
    // to 0 when the invalid page error is found.
    useEffect(() => {
        const handleError = async () => {
            const errorResponse = (dataQuery.error as any).response;
            try {
                const responseClone = errorResponse.clone();
                const response = await responseClone.json();
                if (response.detail === "Invalid page.") {
                    setTableState((ts) => {
                        return {
                            ...ts,
                            pagination: {
                                ...ts.pagination,
                                pageIndex: 0,
                            },
                        };
                    });
                }
            } catch (error) {
                Sentry.captureException(error);
            }
        };
        if (dataQuery.isError) {
            handleError();
        }
    }, [dataQuery, setTableState]);

    // TODO: Add types
    const partialUpdate = useMutation<any, any, any, any>(
        ["partialUpdate"],
        async (params) => {
            return partialUpdateFn(params.itemId, params.value);
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: [dataName, fetchDataOptions],
                });
            },
        },
    );

    // TODO: Add types
    const deleteRow = useMutation<any, any, any, any>(
        ["deleteRow"],
        async (params) => {
            return deleteRowFn(params.itemId);
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: [dataName, fetchDataOptions],
                });
            },
        },
    );

    /**
     * Table configuration
     */
    const table = useReactTable({
        data: dataQuery.data?.results ?? defaultData,
        columns: columns,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (originalRow: any) => getRowId(originalRow),
        defaultColumn: {
            minSize: 0,
            size: Number.MAX_SAFE_INTEGER,
            maxSize: Number.MAX_SAFE_INTEGER,
        },
        // Pagination
        pageCount: dataQuery.data?.numPages,
        onPaginationChange: (updater: any) =>
            setTableState((ts) => {
                return {
                    ...ts,
                    pagination: updater(ts.pagination),
                };
            }),
        manualPagination: true,
        // Row selection
        onRowSelectionChange: (updater: any) =>
            setTableState((ts) => {
                return {
                    ...ts,
                    rowSelection: updater(ts.rowSelection),
                };
            }),
        // Table status
        state: {
            pagination: tableState.pagination,
            columnVisibility: tableState.columnVisibility,
            rowSelection: tableState.rowSelection,
            sorting: tableState.sorting,
        },
        // Update row support
        meta: {
            updateData: (rowIndex, itemId, value) => {
                return partialUpdate.mutateAsync({ rowIndex, itemId, value });
            },
            deleteRow: (rowIndex, itemId) => {
                return deleteRow.mutateAsync({ rowIndex, itemId });
            },
            refresh: async () => {
                dataQuery.refetch();
            },
        },
        // Sorting
        manualSorting: true,
        enableSorting: sortable,
        enableMultiSort: true,
        onSortingChange: (updater: any) =>
            setTableState((ts) => {
                return {
                    ...ts,
                    sorting: updater(ts.sorting),
                };
            }),
        // Filtering
        enableFilters: filterable,
        manualFiltering: true,
        enableColumnFilters: true,
        enableRowSelection: !!selectable,
        enableMultiRowSelection: !!selectable,
    });

    return {
        table,
        dataQuery,
        tableState,
        setTableState,
        dataName,
        getRowId,
    };
};
